import Template from "../../components/Template";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../context/NavigationContext";
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import api from "../../services/api";
import config from "../../config";
import { formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { Link } from "react-router-dom";
import { maskCurrency } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";
import ConfirmationDialog from "../../components/ConfirmationModal";
import SincronizacaoManualNFSe from "../../components/SincronizacaoManualNFSe";

import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
import { lerErroDeDownload } from '../../utils/erroDownload';
// Componente memoizado para linha da tabela - evita re-renders desnecessários
// A seleção vem por prop para que só a linha clicada re-renderize: antes a
// lista inteira de colunas era refeita a cada clique no checkbox.
const TableRow = memo(({ nota, columns, selecionada, onAlternarSelecao }) => {
    return (
        <tr key={nota.id}>
            {columns
                .filter((col) => col.visible)
                .map((col) => (
                    <td
                        key={`${nota.id}-${col.id}`}
                        className="px-4 py-2 border border-slate-200 text-slate-700 text-base"
                    >
                        {col.formatter(nota[col.id], nota, { selecionada, onAlternarSelecao })}
                    </td>
                ))}
        </tr>
    );
}, (prevProps, nextProps) => {
    // Comparação por referência - detecta novos objetos criados por spread (ex: após vincular obra)
    return prevProps.nota === nextProps.nota &&
           prevProps.columns === nextProps.columns &&
           prevProps.selecionada === nextProps.selecionada &&
           prevProps.onAlternarSelecao === nextProps.onAlternarSelecao;
});

const NotasFiscaisServicos = () => {
    const navigate = useNavigate();
    const { updateMenu } = useNavigation();
    const podeVisualizarNota = usePermission("DOWNLOAD_NOTA_SERVICO");
    const podeCriar = usePermission("CRIAR_NOTA_SERVICO");
    const podeDeletar = usePermission("DELETAR_NOTA_SERVICO");

    // Estados para dados principais
    const [fornecedores, setFornecedores] = useState([]);
    const [obras, setObras] = useState([]);
    const [notasFiscais, setNotasFiscais] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [filteredNotas, setFilteredNotas] = useState([]);
    const [selectedNotas, setSelectedNotas] = useState([]);
    const [baixandoNotas, setBaixandoNotas] = useState(false);
    const [selectedForDeletion, setSelectedForDeletion] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    // Estados para sincronização automática
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [showSyncModal, setShowSyncModal] = useState(false);

    // Estados para controle da UI
    const [selectedNota, setSelectedNota] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [columns, setColumns] = useState([]);
    const { filters, setFilters, searchTerm, setSearchTerm, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('notas-fiscais-servico', {
        fornecedor: "",
        obra: "",
        numero: "",
        dataEmissaoInicial: "",
        dataEmissaoFinal: "",
        // o painel tem o filtro de cliente, mas ele faltava aqui: o <select>
        // nascia como campo nao controlado (valor undefined)
        cliente: "",
        status: "",
    });
    const [message, setMessage] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false); // Estado de carregamento

    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);
    // Refs para medir performance
    const renderStartTimeRef = useRef(null);
    const preRenderTimeRef = useRef(null);

    // Define as colunas da tabela
    useEffect(() => {
        setColumns([
            {
                id: "select",
                label: "",
                visible: true,
                headerFormatter: (extras = {}) => (
                    <input
                        type="checkbox"
                        aria-label="Selecionar todas as notas da página"
                        title="Selecionar todas as notas da página"
                        checked={!!extras.todasSelecionadas}
                        ref={(el) => { if (el) el.indeterminate = !!extras.selecaoParcial; }}
                        onChange={extras.onAlternarTodas}
                    />
                ),
                formatter: (value, nota, extras = {}) => (
                    <input
                        type="checkbox"
                        aria-label={`Selecionar nota ${nota?.id}`}
                        checked={!!extras.selecionada}
                        onChange={() => extras.onAlternarSelecao?.(nota?.id)}
                    />
                ),
            },
            {
                id: "id",
                label: "ID",
                visible: true,
                formatter: (value, nota) => {
                    if (nota.xml) {
                        return value;
                    } else {
                        return (
                            <Link to={`/notasfiscaisservicos/editar/${value}`} className="text-stone-600 hover:underline">
                                {value}
                            </Link>
                        )
                    }

                },
            },
            {
                id: "numero",
                label: "Número",
                visible: true,
                formatter: (value) => value,
            },
            {
                id: "fornecedor",
                label: "Fornecedor",
                visible: true,
                formatter: (value, nota) => nota.fornecedorFormatted || "(Sem fornecedor)",
            },
            {
                id: "dataEmissao",
                label: "Data",
                visible: true,
                formatter: (value, nota) => nota.dataEmissaoFormatted || "",
            },
            {
                id: "valorServico",
                label: "Valor Serviço",
                visible: true,
                formatter: (value, nota) => nota.valorServicoFormatted || "",
            },
            {
                id: "valorTotalRet",
                label: "Valor Retido",
                visible: true,
                formatter: (value, nota) => nota.valorTotalRetFormatted || "",
            },
            {
                id: "status",
                label: "Status",
                visible: true,
                formatter: (value) => value || "-",
            },
            {
                id: "obra",
                label: "Obra",
                visible: true,
                formatter: (value, nota) => {
                    // Para NF sem obra vinculada, ocultar obras concluídas (não elegíveis para novo vínculo).
                    // Para NF já vinculada, mostra todas (mantém a atual visível mesmo se concluída).
                    const temObraVinculada = !!nota?.obra?.id;
                    const obrasElegiveis = temObraVinculada
                        ? obras
                        : obras.filter((o) => (o.status?.nome || "").toLowerCase() !== "concluído");
                    return (
                        <select
                            value={nota.obra?.id || ""}
                            onChange={(e) => handleChangeObra(nota.id, e.target.value)}
                            className="border border-slate-300 text-sm rounded"
                        >
                            <option value="">(Sem obra)</option>
                            {obrasElegiveis.map((obra) => (
                                <option key={obra.id} value={obra.id}>
                                    {obra.nomeProjeto.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    );
                },
            },
            {
                id: "pdf",
                label: "PDF",
                visible: true,
                formatter: (value) =>
                    podeVisualizarNota && value && (
                        <div
                            className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                            onClick={() => handleShowPdf(value)}
                        >
                            <img
                                src="images/icon/pdf.png"
                                className="w-10 h-10"
                                title="Ver"
                                alt="Ver"
                            />
                        </div>
                    ),
            },
            {
                id: "xml",
                label: "XML",
                visible: true,
                formatter: (value) => podeVisualizarNota && value && (
                    <div
                        className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                        onClick={() => handleDownloadXml(value)}
                    >
                        <img
                            src="images/icon/xml.png"
                            className="w-10 h-10"
                            title="Ver"
                            alt="Ver"
                        />
                    </div>
                ),
            },
            {
                id: "boleto",
                label: "Boleto",
                visible: true,
                formatter: (value) =>
                    podeVisualizarNota && value && (
                        <div
                            className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                            onClick={() => handleShowBoleto(value)}
                        >
                            <img src="images/icon/boleto.png" className="w-10 h-10" alt="Ver Boleto" />
                        </div>
                    ),
            },
            {
                id: "acoes",
                label: "Ações",
                visible: true,
                formatter: (value, nota) => {
                    {
                        return (
                            podeDeletar && (
                                <button onClick={() => promptDeleteNota(nota.id)} className="text-red-600 hover:underline">
                                    <img src="images/icon/trash.png" className="w-5 h-5" alt="Deletar" />
                                </button>
                            )
                        )
                    }
                },
            },
        ]);
    }, [obras, podeVisualizarNota]);

    // Buscar fornecedores e obras
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [fornRes, obrasRes, clientesRes] = await Promise.all([
                    api.get("/fornecedores/prestadores/todos"),
                    api.get("/obras/listar/todas"),
                    api.get("/clientes/todos")
                ]);
                setFornecedores(fornRes.data);
                setObras(obrasRes.data);
                setClientes(clientesRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    // Função para pré-processar dados e formatar uma única vez
    const preprocessData = useCallback((records) => {
        const startTime = performance.now();
        
        const processedData = records.map(nota => ({
            ...nota,
            // Pré-formatar data
            dataEmissaoFormatted: nota.dataEmissao ? formatDateWithoutHour(nota.dataEmissao) : "",
            // Pré-formatar fornecedor
            fornecedorFormatted: (() => {
                if (!nota.fornecedor) return "(Sem fornecedor)";
                const nome = nota.fornecedor?.razaoSocial?.toUpperCase() || nota.fornecedor?.nomeFornecedor?.toUpperCase() || "";
                const cpfCnpj = nota.fornecedor?.cpfCnpj || "";
                return cpfCnpj ? `${nome} - ${cpfCnpj}` : nome || "(Sem fornecedor)";
            })(),
            // Pré-formatar valor do serviço
            valorServicoFormatted: (() => {
                if (!nota.valorServico) return "";
                const num = parseFloat(nota.valorServico);
                return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            })(),
            // Pré-formatar valor retido
            valorTotalRetFormatted: (() => {
                if (!nota.valorTotalRet) return "";
                const num = parseFloat(nota.valorTotalRet);
                return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            })(),
        }));
        
        const endTime = performance.now();
        console.log(`⚡ Pré-processamento NFSe: ${(endTime - startTime).toFixed(2)}ms (${records.length} registros)`);
        
        return processedData;
    }, []);

    // Buscar as notas fiscais de serviço
    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchNotasFiscais(currentPage, pageSize);
    }, [currentPage]);

    // Atualiza os registros filtrados conforme o termo de pesquisa
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredNotas(notasFiscais);
        } else {
            setFilteredNotas(
                notasFiscais.filter((nota) =>
                    nota.numero.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [searchTerm, notasFiscais]);

    // Consulta O(1) por linha em vez de um `includes` por linha.
    const idsSelecionados = useMemo(() => new Set(selectedNotas), [selectedNotas]);

    // Quantas das notas visíveis estão marcadas — alimenta o checkbox do cabeçalho.
    const selecaoDaPagina = useMemo(() => {
        const marcadas = filteredNotas.filter((nota) => idsSelecionados.has(nota.id)).length;
        return {
            todasSelecionadas: filteredNotas.length > 0 && marcadas === filteredNotas.length,
            selecaoParcial: marcadas > 0 && marcadas < filteredNotas.length,
        };
    }, [filteredNotas, idsSelecionados]);

    // Marca ou desmarca todas as notas visíveis, preservando a seleção feita
    // em outras páginas.
    const alternarSelecaoDaPagina = useCallback(() => {
        const idsDaPagina = filteredNotas.map((nota) => nota.id).filter((id) => Number.isInteger(id));
        setSelectedNotas((prev) => {
            const todasMarcadas = idsDaPagina.length > 0 && idsDaPagina.every((id) => prev.includes(id));
            if (todasMarcadas) return prev.filter((id) => !idsDaPagina.includes(id));
            return [...new Set([...prev, ...idsDaPagina])];
        });
    }, [filteredNotas]);

    // Filtros efetivamente aplicados pelo botao "Filtrar". Sao reenviados em toda
    // troca de pagina e de "resultados por pagina": antes a paginacao refazia a
    // busca sem filtro nenhum e a lista voltava silenciosamente ao conjunto completo.
    const appliedFiltersRef = useRef({});

    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchNotasFiscais = async (page, size) => {
        const apiStartTime = performance.now();
        
        try {
            setLoading(true);
            renderStartTimeRef.current = performance.now();
            
            // Aviso para grandes datasets
            if (size > 1000) {
                console.warn(`⚠️ Carregando ${size} registros por página - pode demorar mais que o esperado`);
            }
            
            const params = new URLSearchParams({ page, pageSize: size });
            Object.entries(appliedFiltersRef.current).forEach(([chave, valor]) => {
                if (valor !== undefined && valor !== null && valor !== "") params.set(chave, valor);
            });
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/notasfiscaisservicos?${params.toString()}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiEndTime = performance.now();
            const apiTime = apiEndTime - apiStartTime;
            console.log(`🌐 API NFSe: ${apiTime.toFixed(2)}ms (${response.data.records.length} registros)`);
            
            // Pré-processar dados
            const processedRecords = preprocessData(response.data.records);
            
            preRenderTimeRef.current = performance.now();
            
            setNotasFiscais(processedRecords);
            setTotalRecords(response.data.total);
            setFilteredNotas(processedRecords);
            setPageSize(size);
            
            const stateUpdateTime = performance.now();
            console.log(`📊 State update NFSe: ${(stateUpdateTime - preRenderTimeRef.current).toFixed(2)}ms`);
            
            // Aguardar renderização completa
            setTimeout(() => {
                const totalTime = performance.now() - renderStartTimeRef.current;
                const pureRenderTime = performance.now() - preRenderTimeRef.current;
                const numCells = response.data.records.length * 11; // 11 colunas visíveis
                const timePerCell = pureRenderTime / numCells;
                
                console.log(`🎨 Renderização pura NFSe: ${pureRenderTime.toFixed(2)}ms`);
                console.log(`⏱️ Tempo total NFSe: ${totalTime.toFixed(2)}ms`);
                console.log(`📏 Células renderizadas: ${numCells.toLocaleString()} (${response.data.records.length} linhas × 11 colunas)`);
                console.log(`⚡ Tempo por célula: ${timePerCell.toFixed(2)}ms`);
                
                // Análise automática
                console.log("\n📊 === ANÁLISE DE PERFORMANCE NFSe ===");
                console.log(`API: ${apiTime.toFixed(0)}ms (${((apiTime/totalTime)*100).toFixed(1)}%)`);
                console.log(`Pré-processamento: ${(preRenderTimeRef.current - apiEndTime).toFixed(0)}ms (${(((preRenderTimeRef.current - apiEndTime)/totalTime)*100).toFixed(1)}%)`);
                console.log(`State Update: ${(stateUpdateTime - preRenderTimeRef.current).toFixed(2)}ms (${(((stateUpdateTime - preRenderTimeRef.current)/totalTime)*100).toFixed(1)}%)`);
                console.log(`Renderização DOM: ${pureRenderTime.toFixed(0)}ms (${((pureRenderTime/totalTime)*100).toFixed(1)}%)`);
                
                if (pureRenderTime > 5000) {
                    console.warn("⚠️ CRÍTICO: Renderização muito lenta (>5s)! Considere:");
                    console.warn("  - Reduzir registros por página");
                    console.warn("  - Implementar virtualização (react-window)");
                    console.warn("  - Simplificar colunas/formatações");
                } else if (pureRenderTime > 2000) {
                    console.warn("⚠️ ATENÇÃO: Renderização lenta (>2s). Considere otimizar.");
                }
                
                setLoading(false);
            }, 100);
            
        } catch (error) {
            console.error("Erro ao carregar notas fiscais de serviço:", error);
            setLoading(false);
        }
    };

    const fetchFilteredNotasFiscais = async (query) => {
        const apiStartTime = performance.now();
        
        try {
            setLoading(true);
            renderStartTimeRef.current = performance.now();
            
            const params = new URLSearchParams(query).toString();
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/notasfiscaisservicos?${params}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiEndTime = performance.now();
            const apiTime = apiEndTime - apiStartTime;
            console.log(`🔍 API filtrada NFSe: ${apiTime.toFixed(2)}ms (${response.data.records.length} registros)`);
            
            // Pré-processar dados
            const processedRecords = preprocessData(response.data.records);
            
            preRenderTimeRef.current = performance.now();
            
            setNotasFiscais(processedRecords);
            setTotalRecords(response.data.total);
            setCurrentPage(1);
            setFilteredNotas(processedRecords);
            
            const stateUpdateTime = performance.now();
            console.log(`📊 State update filtrada NFSe: ${(stateUpdateTime - preRenderTimeRef.current).toFixed(2)}ms`);
            
            // Aguardar renderização completa
            setTimeout(() => {
                const totalTime = performance.now() - renderStartTimeRef.current;
                const pureRenderTime = performance.now() - preRenderTimeRef.current;
                
                console.log(`🎨 Renderização pura filtrada NFSe: ${pureRenderTime.toFixed(2)}ms`);
                console.log(`⏱️ Tempo total filtrado NFSe: ${totalTime.toFixed(2)}ms`);
                
                setLoading(false);
            }, 100);
            
        } catch (error) {
            console.error("Erro ao aplicar filtros:", error);
            setLoading(false);
        }
    };

    const handleNavigateCadastrar = async (e) => {
        e.preventDefault();
        try {
            navigate("/notasfiscaisservicos/cadastrar");
        } catch (err) {
            console.error("Notas Fiscais de Serviço Error:", err);
            alert(err.response?.data?.msg || "Erro ao ir para o cadastro");
        }
    };

    const handleCloseDialog = () => {
        setSelectedNota(null);
        setShowDialog(false);
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    // Função para visualizar o boleto
    const handleShowBoleto = (boletoPath) => {
        if (!boletoPath) return;
        window.open(config.urlArquivo(boletoPath), "_blank");
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Alterna pelo estado atual, e não por `e.target.checked`: assim um clique
    // repetido nunca insere o mesmo id duas vezes na seleção.
    const alternarSelecao = useCallback((notaId) => {
        const id = Number(notaId);
        if (!Number.isInteger(id)) return;
        setSelectedNotas((prev) => (
            prev.includes(id) ? prev.filter((selecionado) => selecionado !== id) : [...prev, id]
        ));
    }, []);

    const downloadSelectedNotas = async () => {
        if (selectedNotas.length === 0) {
            setMessage("Selecione ao menos uma nota para baixar.");
            return;
        }
        setBaixandoNotas(true);
        try {
            const response = await api.post(
                "/notasfiscaisservicos/notas/download",
                { ids: selectedNotas },
                { responseType: "blob" }
            );
            const blob = new Blob([response.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "notasServicoSelecionadas.zip");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Erro ao baixar ZIP:", error);
            // Com `responseType: blob` o motivo do erro chega como Blob: sem ler o
            // corpo, toda falha virava o mesmo "verifique o console".
            const motivo = await lerErroDeDownload(error, "Não foi possível baixar as notas selecionadas.");
            setMessage(motivo);
        } finally {
            setBaixandoNotas(false);
        }
    };

    const handleChangeObra = async (notaId, obraId) => {
        try {
            const obraIdNum = obraId ? Number(obraId) : null;
            await api.patch(`/notasfiscaisservicos/${notaId}/vincularObra`, {
                obraId: obraIdNum,
            });
            setNotasFiscais((prevNotas) =>
                prevNotas.map((nota) =>
                    nota.id === notaId
                        ? { ...nota, obra: obras.find((o) => o.id === obraIdNum) || null }
                        : nota
                )
            );
            alert("Obra vinculada com sucesso!");
        } catch (error) {
            console.error("Erro ao vincular obra à nota:", error);
            alert("Não foi possível vincular obra. Verifique o console.");
        }
    };

    // Função para iniciar a deleção (exibe modal de confirmação)
    const promptDeleteNota = (id) => {
        setSelectedForDeletion(id);
        setShowConfirmDelete(true);
    };

    // Função para confirmar a deleção
    const handleDeleteNota = async () => {
        try {
            await api.delete(`/notasfiscaisservicos/${selectedForDeletion}`);
            setShowConfirmDelete(false);
            setMessage("Nota fiscal de serviço deletada com sucesso!");
            // Atualiza a listagem
            fetchNotasFiscais(currentPage, pageSize);
        } catch (error) {
            console.error("Erro ao deletar NFSe:", error);
            setMessage("Erro ao deletar a nota fiscal de serviço.");
        }
    };

    // Função para cancelar a deleção
    const handleCancelDelete = () => {
        setShowConfirmDelete(false);
        setSelectedForDeletion(null);
    };

    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const pageWindowSize = 5;
    // Mantem a janela cheia tambem nas ultimas paginas (antes ela encolhia).
    const startPage = Math.max(1, Math.min(currentPage - Math.floor(pageWindowSize / 2), totalPages - pageWindowSize + 1));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
    );

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    const handleMenuClick = (event) => {
        event.stopPropagation();
    };

    // Função para sincronizar NFSe automaticamente
    const handleSincronizarNFSe = async () => {
        setIsSyncing(true);
        setSyncMessage('Sincronizando notas fiscais de serviço...');
        setShowSyncModal(true);

        try {
            const response = await api.post('/notasfiscaisservicos/sincronizar');
            const resultado = response.data;

            // Monta mensagem de resultado
            let mensagem = `✅ Sincronização concluída!\n\n`;
            mensagem += `📊 Total processado: ${resultado.total || 0}\n`;
            mensagem += `🆕 Novas notas: ${resultado.novas || 0}\n`;
            mensagem += `🔄 Duplicadas: ${resultado.duplicadas || 0}\n`;
            
            if (resultado.erros && resultado.erros > 0) {
                mensagem += `❌ Erros: ${resultado.erros}\n`;
            }

            setSyncMessage(mensagem);

            // Recarrega a lista de notas após sincronização
            if (resultado.novas > 0) {
                setTimeout(() => {
                    fetchNotasFiscais(currentPage, pageSize);
                }, 2000);
            }

        } catch (error) {
            console.error('Erro na sincronização:', error);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               'Erro ao sincronizar notas fiscais de serviço';
            
            setSyncMessage(`❌ ${errorMessage}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const closeSyncModal = () => {
        setShowSyncModal(false);
        setSyncMessage('');
    };

    const applyFilters = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = { ...filters, page: 1, pageSize };
        fetchFilteredNotasFiscais(query);
    };

    const clearFilters = () => {
        clearPersistedState();
        appliedFiltersRef.current = {};
        setSearchTerm("");
        setCurrentPage(1);
        setFilters({
            fornecedor: "",
            obra: "",
            numero: "",
            dataEmissaoInicial: "",
            dataEmissaoFinal: "",
            cliente: "",
            status: "",
        });
        fetchNotasFiscais(1, pageSize);
    };

    const handleSearch = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = { ...filters, page: 1, pageSize };
        fetchFilteredNotasFiscais(query);
    };

    const exportToExcel = () => {
        if (notasFiscais.length === 0) {
            setMessage("Não há dados para exportar!");
            return;
        }

        const monetaryColumns = ['valorServico', 'valorTotalRet']; // substitua pelos IDs reais

        const dataToExport = notasFiscais.map((nota) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible && col.label !== "PDF" && col.label !== "XML") {
                    // Formatters de coluna que devolvem JSX (links, botoes, checkboxes)
                    // viravam "[object Object]" na planilha: nesses casos exporta o valor bruto.
                    let value = col.formatter ? col.formatter(nota[col.id], nota) : nota[col.id];
                    if (value && typeof value === 'object') value = nota[col.id];

                    if (React.isValidElement(value)) {
                        value = nota[col.id]; // ou ajuste conforme necessário
                    }

                    // Remove formatação monetária (p.ex. 'R$', '.', ',') e converte para número
                    if (monetaryColumns.includes(col.id)) {
                        value = Number(String(value)
                            .replace(/[R$\s.]/g, '')
                            .replace(',', '.')
                        );
                    }

                    row[col.label] = value;
                }
            });
            return row;
        });
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Formata células monetárias explicitamente como números no Excel
        const columnLabels = columns.filter(col => col.visible).map(col => col.label);
        monetaryColumns.forEach((colId) => {
            const colIndex = columnLabels.indexOf(columns.find(c => c.id === colId)?.label);
            if (colIndex >= 0) {
                const columnLetter = XLSX.utils.encode_col(colIndex);
                const range = XLSX.utils.decode_range(worksheet['!ref']);

                for (let i = range.s.r + 1; i <= range.e.r; i++) {
                    const cellAddress = `${columnLetter}${i + 1}`;
                    if (worksheet[cellAddress]) {
                        worksheet[cellAddress].t = 'n';
                        worksheet[cellAddress].z = '#,##0.00';
                    }
                }
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Notas Fiscais Serviço");
        XLSX.writeFile(workbook, "notasFiscaisServicos.xlsx");
    };

    const handleDownloadXml = (xmlPath) => {
        if (!xmlPath) {
            console.warn("Não existe caminho para o XML.");
            return;
        }
        const url = config.urlArquivo(xmlPath);
        window.open(url, "_blank");
    };

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium font-['Inter']">
                        Notas Fiscais de Serviço
                    </div>
                    <div className="flex gap-2">
                        {/* Botão de Sincronização Manual por Chave */}
                        <SincronizacaoManualNFSe
                            onSuccess={(successMessage) => {
                                setMessage(successMessage);
                                // Recarregar lista após sincronização bem-sucedida
                                setTimeout(() => {
                                    fetchNotasFiscais(currentPage, pageSize);
                                }, 2000);
                            }}
                            onError={(errorMessage) => {
                                setMessage(errorMessage);
                            }}
                        />
                        
                        {/* Botão Cadastrar Manual */}
                        {podeCriar && (
                            <button
                                className="px-4 py-2 bg-stone-600 rounded-md text-white"
                                onClick={handleNavigateCadastrar}
                            >
                                Cadastrar
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium font-['Inter']">
                            Ver
                        </div>
                        <select
                            className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10);
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                                fetchNotasFiscais(1, newPageSize);
                            }}
                            disabled={loading}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={150}>150</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                            <option value={2000}>2000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium font-['Inter']">
                            Resultados
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div
                            ref={buttonRef}
                            className={`relative flex items-center gap-1 ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => !loading && setMenuOpen(menuOpen === "colunas" ? null : "colunas")}
                        >
                            <div className="flex items-center gap-2">
                                <img
                                    className="w-6 h-6"
                                    src="images/icon/columns.png"
                                    alt="Colunas"
                                />
                                <div className="text-stone-600 text-sm font-medium font-['Inter']">
                                    Colunas
                                </div>
                            </div>
                            <img
                                className="w-6 h-4 px-1"
                                src="images/icon/chevron-down.png"
                                alt="Dropdown"
                            />
                            {menuOpen === "colunas" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick}
                                >
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div key={col.id} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() => toggleColumnVisibility(col.id)}
                                                />
                                                <label
                                                    htmlFor={col.id}
                                                    className="text-sm text-gray-700"
                                                >
                                                    {col.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div
                            ref={buttonFiltrarRef}
                            className="relative flex items-center gap-1 cursor-pointer"
                            onClick={() =>
                                setMenuOpen(menuOpen === "filtro" ? null : "filtro")
                            }
                        >
                            <div className="flex items-center gap-2">
                                <img
                                    className="w-8 h-8"
                                    src="images/icon/filter.png"
                                    alt="Filtrar"
                                />
                                <div className="text-stone-600 text-sm font-medium font-['Inter']">
                                    Filtrar
                                </div>
                            </div>
                            <img
                                className="w-6 h-4 px-1"
                                src="images/icon/chevron-down.png"
                                alt="Dropdown"
                            />
                            {menuOpen === "filtro" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick}
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-2">
                                            <div className="grow text-stone-800 text-xl font-medium">
                                                Filtrar
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Cliente
                                            </div>
                                            <select
                                                name="cliente"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.cliente}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, cliente: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {clientes.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">
                                                Fornecedor
                                            </div>
                                            <select
                                                name="fornecedor"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.fornecedor}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, fornecedor: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {fornecedores.map((f) => (
                                                    <option key={f.id} value={f.id}>
                                                        {f.nomeFornecedor.toUpperCase() + " - " + f.cpfCnpj}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">
                                                Obra
                                            </div>
                                            <select
                                                name="obra"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.obra}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, obra: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {obras.map((o) => (
                                                    <option key={o.id} value={o.id}>
                                                        {o.nomeProjeto.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">
                                                Status
                                            </div>
                                            <input
                                                type="text"
                                                name="status"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o status (ex: Emitida)"
                                                value={filters.status}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, status: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">
                                                Número
                                            </div>
                                            <input
                                                type="text"
                                                name="numero"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o número da nota"
                                                value={filters.numero}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, numero: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">
                                                Emissão
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    name="dataEmissaoInicial"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataEmissaoInicial}
                                                    onChange={(e) =>
                                                        setFilters({
                                                            ...filters,
                                                            dataEmissaoInicial: e.target.value,
                                                        })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="dataEmissaoFinal"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataEmissaoFinal}
                                                    onChange={(e) =>
                                                        setFilters({
                                                            ...filters,
                                                            dataEmissaoFinal: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between mt-4 gap-4">
                                            <button
                                                className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex items-center justify-center gap-2"
                                                onClick={clearFilters}
                                            >
                                                <img
                                                    className="w-4 h-4"
                                                    src="images/icon/eraser.png"
                                                    alt="Limpar"
                                                />
                                                <div className="text-stone-500 text-sm font-medium">
                                                    Limpar
                                                </div>
                                            </button>
                                            <button
                                                className="px-4 py-2 w-full bg-stone-600 rounded-md flex items-center justify-center gap-2"
                                                onClick={applyFilters}
                                            >
                                                <img
                                                    className="w-4 h-4"
                                                    src="images/icon/filter-white.png"
                                                    alt="Filtrar"
                                                />
                                                <div className="text-white text-sm font-medium">
                                                    Filtrar
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {!isSearchActive ? (
                                <div
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => setIsSearchActive(true)}
                                >
                                    <img
                                        className="w-10 h-10"
                                        src="images/icon/search.png"
                                        alt="Pesquisar"
                                    />
                                    <div className="text-stone-600 text-sm font-medium">
                                        Pesquisar
                                    </div>
                                </div>
                            ) : (
                                <div className="h-10 inline-flex items-center gap-2">
                                    <div className="h-9 px-3 py-2 bg-white rounded-md border border-slate-300 flex items-center">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar"
                                            className="w-full text-slate-400 text-base focus:outline-none"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <img
                                            className="w-6 h-6 cursor-pointer"
                                            src="images/icon/close.png"
                                            alt="Fechar"
                                            onClick={() => setIsSearchActive(false)}
                                        />
                                    </div>
                                    <button
                                        className="px-4 py-2 bg-stone-600 rounded-md flex items-center gap-2"
                                        onClick={handleSearch}
                                    >
                                        <img
                                            className="w-4 h-4"
                                            src="images/icon/search-white.png"
                                            alt="Pesquisar"
                                        />
                                        <div className="text-white text-sm font-medium">
                                            Pesquisar
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        {usePermission("EXPORTAR_NOTA") && (
                            <button
                                type="button"
                                className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 items-center gap-2 cursor-pointer"
                                onClick={exportToExcel}
                            >
                                <img
                                    className="w-4 h-4"
                                    src="images/icon/upload.png"
                                    alt="Exportar"
                                />
                                <div className="text-stone-600 text-sm font-medium">
                                    Exportar
                                </div>
                            </button>
                        )}
                        {usePermission("DOWNLOAD_NOTA_SERVICO") && (
                            <button
                                type="button"
                                data-testid="baixar-notas"
                                className={`flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 items-center gap-2 ${baixandoNotas ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                onClick={downloadSelectedNotas}
                                disabled={baixandoNotas}
                            >
                                <img
                                    className="w-4 h-4"
                                    src="images/icon/download-black.png"
                                    alt="Download"
                                />
                                <div className="text-stone-600 text-sm font-medium">
                                    {baixandoNotas
                                        ? "Baixando..."
                                        : `Download Notas${selectedNotas.length > 0 ? ` (${selectedNotas.length})` : ""}`}
                                </div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Loading Indicator */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-stone-600"></div>
                        <p className="text-stone-600 text-lg font-medium">
                            Carregando {pageSize > 100 ? 'muitos' : ''} registros... 
                            {pageSize > 100 && <span className="text-sm block text-slate-500 mt-1">Isso pode levar alguns segundos</span>}
                        </p>
                    </div>
                )}

                {/* Tabela de Dados */}
                {!loading && (
                    <div className="overflow-x-auto md:overflow-x-visible">
                        <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                {columns
                                    .filter((col) => col.visible)
                                    .map((col) => (
                                        <th
                                            key={col.id}
                                            className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold"
                                        >
                                            {col.headerFormatter
                                                ? col.headerFormatter({
                                                    ...selecaoDaPagina,
                                                    onAlternarTodas: alternarSelecaoDaPagina,
                                                })
                                                : col.label}
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredNotas.map((nota) => (
                                <TableRow
                                    key={nota.id}
                                    nota={nota}
                                    columns={columns}
                                    selecionada={idsSelecionados.has(nota.id)}
                                    onAlternarSelecao={alternarSelecao}
                                />
                            ))}
                        {totalRecords === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.filter((col) => col.visible).length || 1}
                                    className="px-4 py-6 border border-slate-200 text-center text-slate-500 text-base"
                                >
                                    Nenhum registro encontrado.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
                )}

                <div className="flex items-center justify-end gap-2.5">
                    <div className="grow text-slate-500 text-base font-normal">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            className={`w-10 h-10 rounded border border-[#bebebe] flex justify-center items-center ${currentPage === 1 || loading
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-gray-300 cursor-pointer"
                                }`}
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img
                                className="w-6 h-6"
                                src="images/icon/keyboard-arrow-left.png"
                                alt="Anterior"
                            />
                        </button>
                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded flex justify-center items-center text-base font-semibold ${page === currentPage
                                    ? "bg-stone-600 text-white"
                                    : "bg-gray-200 text-gray-600"
                                    }`}
                                onClick={() => handlePageChange(page)}
                                disabled={loading}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className={`w-10 h-10 rounded border border-[#bebebe] flex justify-center items-center ${currentPage === totalPages || loading
                                ? "opacity-30 cursor-not-allowed"
                                : "cursor-pointer"
                                }`}
                            disabled={currentPage >= totalPages || totalPages === 0 || loading}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img
                                className="w-6 h-6"
                                src="images/icon/keyboard-arrow-right.png"
                                alt="Próximo"
                            />
                        </button>
                    </div>
                </div>
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex flex-col justify-end items-end gap-10">
                        <div className="rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <a
                                    href={config.urlArquivo(selectedPdf)}
                                    download
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        className="w-6 h-6"
                                        src="images/icon/download.png"
                                        alt="Download PDF"
                                    />
                                </a>
                                <img
                                    className="w-6 h-6 cursor-pointer"
                                    src="images/icon/x.png"
                                    alt="Fechar"
                                    onClick={handleCloseDialog}
                                />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}
                <AlertDialog
                    isVisible={message !== ""}
                    message={message}
                    onClose={() => setMessage("")}
                />

                {/* Modal de Confirmação para Deleção */}
                {showConfirmDelete && (
                    <ConfirmationDialog
                        isVisible={showConfirmDelete}
                        message="Deseja realmente deletar esta nota fiscal de serviço? Esta ação removerá todos os arquivos relacionados."
                        onConfirm={handleDeleteNota}
                        onCancel={handleCancelDelete}
                        textButtonConfirm='Deletar'
                    />
                )}

                {/* Modal de Sincronização */}
                {showSyncModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Sincronização de NFSe
                                </h3>
                                {!isSyncing && (
                                    <button
                                        onClick={closeSyncModal}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>

                            <div className="mb-4">
                                <div className={`p-4 rounded-md whitespace-pre-line ${
                                    isSyncing 
                                        ? 'bg-blue-50 border border-blue-200 text-blue-700' 
                                        : syncMessage.includes('❌')
                                            ? 'bg-red-50 border border-red-200 text-red-700'
                                            : 'bg-green-50 border border-green-200 text-green-700'
                                }`}>
                                    {isSyncing && (
                                        <div className="flex items-center mb-2">
                                            <svg className="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Sincronizando com Qive...</span>
                                        </div>
                                    )}
                                    <div className="text-sm">{syncMessage}</div>
                                </div>
                            </div>

                            {!isSyncing && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={closeSyncModal}
                                        className="px-4 py-2 bg-stone-600 hover:bg-stone-700 text-white rounded-md transition-colors"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Template>
    );
};

export default NotasFiscaisServicos;
